// A klinikai összehasonlító görbe kiválasztása és interpolációja —
// docs/KLINIKAI_ADATOK.md 4-5. szakasza szerint: hiányzó gyógyszer vagy
// terápia-ok esetén NEM szabad görbét mutatni, mert a rossz referencia
// rosszabb, mint az üres állapot.

import { CLINICAL_REFERENCE, ClinicalReferenceBranch, ClinicalDoseEndpoint } from "../data/clinicalReference";

export type TherapyReason = "cukorbetegseg" | "testsulycsokkenes" | "mindketto" | null | undefined;

/**
 * A megfelelő klinikai referencia-ág kiválasztása gyógyszer + terápia-ok
 * alapján. Hiányzó gyógyszer vagy hiányzó terápia-ok esetén undefined —
 * ilyenkor a hívónak üres állapotot kell mutatnia.
 *
 * "Mindkettő" válasznál a cukorbetegség-ágra esik (2026-08-17-i döntés,
 * docs/FOGALMAK.md #7): a T2D-populáció a klinikailag megfelelő
 * összehasonlítás, és konzisztens a glükóz-toggle logikájával (Onboarding.tsx
 * `offerGlucose` is a "mindkettő"-t a cukorbetegség-ággal kezeli együtt).
 */
export function selectClinicalBranch(
  gyogyszer: string | null | undefined,
  therapyReason: TherapyReason
): ClinicalReferenceBranch | undefined {
  if (!gyogyszer || !therapyReason) return undefined;
  const population = therapyReason === "testsulycsokkenes" ? "testsulycsokkenes" : "cukorbetegseg";
  return CLINICAL_REFERENCE.find((b) => b.gyogyszer === gyogyszer && b.population === population);
}

/**
 * A felhasználó jelenlegi dózisához legközelebb eső vizsgálati dózis-ág.
 * A vizsgálatok nem minden prescribálható dózist tesztelnek (pl. a
 * tirzepatid 2,5/7,5/12,5 mg-os titrálási lépcsői nincsenek a forrásban).
 * Egyenlő távolságnál az alacsonyabb (konzervatívabb, nem túlígérő) dózist
 * választja. Hiányzó jelenlegi dózisnál a legmagasabb (fenntartó) dózist adja.
 */
export function selectClosestDose(
  branch: ClinicalReferenceBranch,
  currentDoseMg: number | null | undefined
): ClinicalDoseEndpoint {
  if (!currentDoseMg) return branch.doses[branch.doses.length - 1];
  let closest = branch.doses[0];
  let closestDiff = Math.abs(closest.doseMg - currentDoseMg);
  for (const d of branch.doses) {
    const diff = Math.abs(d.doseMg - currentDoseMg);
    if (diff < closestDiff || (diff === closestDiff && d.doseMg < closest.doseMg)) {
      closest = d;
      closestDiff = diff;
    }
  }
  return closest;
}

export type ClinicalEmptyStateReason = "missing_therapy_reason" | "missing_drug";

/**
 * Az üres állapot pontos oka, amikor `selectClinicalBranch` undefined-et ad —
 * a Fooldal/Statisztika ez alapján dönti el, melyik üzenetet mutassa (a két ok
 * nem mosódhat össze: az egyikhez van tennivaló a Profilban, a másikhoz nincs
 * egyértelmű, önmagában elvégezhető lépés).
 */
export function selectClinicalEmptyStateReason(
  gyogyszer: string | null | undefined,
  _therapyReason: TherapyReason
): ClinicalEmptyStateReason {
  const hasValidDrug = gyogyszer === "tirzepatide" || gyogyszer === "semaglutide";
  return hasValidDrug ? "missing_therapy_reason" : "missing_drug";
}

/**
 * Heti bontású sorozat 0%-tól (0. hét) a végpont-értékig, lineáris
 * interpolációval — a forrás csak a végpontot közli, a köztes pontok nem
 * publikáltak (docs/KLINIKAI_ADATOK.md fejléc-figyelmeztetése).
 */
export function interpolateWeeklySeries(
  weeks: number,
  endpointPct: number,
  stepWeeks: number = 4
): { week: number; pct: number }[] {
  const points: { week: number; pct: number }[] = [];
  for (let w = 0; w <= weeks; w += stepWeeks) {
    points.push({ week: w, pct: w === 0 ? 0 : (endpointPct / weeks) * w });
  }
  if (points[points.length - 1]?.week !== weeks) {
    points.push({ week: weeks, pct: endpointPct });
  }
  return points;
}

// ─── Platform-függő címkézés (2026-09-01, App Store 1.4.2 / APL531603) ───
//
// A klinikai komparátor nem SZÁMOL dózist, de mg-értéket ír a grafikonra: a
// legenda-chipre, a vonal nevére (tooltip) és a komparátor-készítmény
// címkéjére. Natívon ezek helyett semleges felirat megy — a görbe marad,
// csak mg nélkül.
//
// Miért itt: a Fooldal és a Statisztika ugyanezt a négy felületet rendereli,
// és a CLAUDE.md 10. szabálya szerint a felhasználónak megjelenített szöveg
// sem élhet két külön inline másolatban.

/** Natívon a dózis-referenciavonal semleges neve (mg nélkül). */
export const NEUTRAL_REFERENCE_LABEL = "Vizsgálati átlag";

/**
 * A referenciavonal címkéje: weben "10 mg", natívon semleges felirat.
 */
export function formatReferenceDoseLabel(doseMg: number, isNative: boolean): string {
  return isNative ? NEUTRAL_REFERENCE_LABEL : `${doseMg} mg`;
}

/**
 * Dózis-token eltávolítása egy címkéből: "Ozempic 1 mg" → "Ozempic".
 * A komparátor-címke az adatban tartalmazhat mg-ot (clinicalReference.ts),
 * ezért nem elég a referenciavonalat kezelni.
 */
export function stripDoseFromLabel(label: string): string {
  return label.replace(/\s*[\d]+([.,][\d]+)?\s*mg\b/gi, "").trim();
}

/** A komparátor-készítmény címkéje: weben változatlan, natívon mg nélkül. */
export function formatComparatorLabel(label: string, isNative: boolean): string {
  return isNative ? stripDoseFromLabel(label) : label;
}

/**
 * A képaláírás "származtatott" mondata. A webes szöveg a "dózisonkénti
 * érték"-re hivatkozik — natívon ez is mg-fogalom, ezért semlegesítjük.
 */
export function formatDerivedNote(isNative: boolean): string {
  return isNative
    ? " A vizsgálati érték a publikált abszolút adatokból származtatott közelítés."
    : " A dózisonkénti érték a vizsgálat abszolút adataiból származtatott közelítés.";
}
